import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { lerContexto } from '../api';
import { contextosDe } from '../contexto';
import { Logo } from '../componentes/Logo';
import { Menu } from '../componentes/ui';
import type { Perfil } from '../tipos';

interface Item {
  chave: string;
  rotulo: string;
  caminho?: string;
  perfis: Perfil[];
}

/**
 * O menu não é uma constante: o Conti9 tem quatro perfis e uma pessoa pode
 * ter mais de um (contador num escritório e cliente numa empresa, por
 * exemplo) — mas só usa UM por vez, o que ela escolheu em /escolher. Item
 * sem `caminho` é tela que ainda não existe — fica visível e desabilitada,
 * porque some-e-aparece dá a impressão de sistema quebrado.
 */
const ITENS: Item[] = [
  { chave: 'visao', rotulo: 'Visão geral', caminho: '/painel', perfis: ['admin'] },
  { chave: 'pendencias', rotulo: 'Pendências', caminho: '/painel/pendencias', perfis: ['admin'] },
  { chave: 'escritorios', rotulo: 'Escritórios', caminho: '/painel/escritorios', perfis: ['admin'] },
  { chave: 'empresas', rotulo: 'Empresas', caminho: '/painel/empresas', perfis: ['admin'] },
  { chave: 'instalacoes', rotulo: 'Instalações', caminho: '/painel/instalacoes', perfis: ['admin'] },
  { chave: 'pessoas', rotulo: 'Pessoas', caminho: '/painel/pessoas', perfis: ['admin'] },
  { chave: 'comissoes', rotulo: 'Comissões', caminho: '/painel/comissoes', perfis: ['admin'] },
  { chave: 'config', rotulo: 'Configurações', caminho: '/painel/config', perfis: ['admin'] },
  { chave: 'documentos', rotulo: 'Documentos', caminho: '/painel/documentos', perfis: ['contador', 'cliente', 'admin'] },
  { chave: 'fechamento', rotulo: 'Fechamento', perfis: ['contador'] },
  { chave: 'relatorios', rotulo: 'Relatórios', perfis: ['contador', 'cliente'] },
  { chave: 'chamados', rotulo: 'Chamados', caminho: '/painel/chamados', perfis: ['contador', 'cliente', 'softhouse'] },
  { chave: 'indicacoes', rotulo: 'Indique e ganhe', caminho: '/painel/indicacoes', perfis: ['contador'] },
];

const ROTULO_PERFIL: Record<Perfil, string> = {
  admin: 'Administrador', contador: 'Contador', cliente: 'Cliente', softhouse: 'Suporte',
};

export function Casca() {
  const { usuario, sair } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [aberto, setAberto] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora: menu que só fecha no próprio botão prende quem
  // abriu e ainda cobre o conteúdo que a pessoa quer ler.
  useEffect(() => {
    if (!menu) return;
    function fora(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [menu]);

  const perfis = usuario?.perfis ?? [];
  const contexto = lerContexto();
  // Chapéu ativo (o que a pessoa escolheu em /escolher), não a soma de
  // tudo que ela poderia acessar -- é isso que faz "entrar como contador"
  // de fato esconder o que é só de admin.
  const perfilAtivo: Perfil = contexto
    ? contexto.tipo === 'admin' ? 'admin'
      : contexto.tipo === 'escritorio' ? 'contador'
      : contexto.tipo === 'softhouse' ? 'softhouse'
      : 'cliente'
    : (perfis[0] ?? 'cliente');
  const opcoes = usuario ? contextosDe(usuario) : [];

  const itens = ITENS.filter((i) => i.perfis.includes(perfilAtivo));

  // Derivado do endereço, para continuar certo depois de recarregar ou de
  // usar voltar/avançar do navegador.
  const ativo = (() => {
    if (pathname.startsWith('/painel/pendencias')) return 'pendencias';
    if (pathname.startsWith('/painel/documentos')) return 'documentos';
    if (pathname.startsWith('/painel/escritorios')) return 'escritorios';
    if (pathname.startsWith('/painel/empresas')) return 'empresas';
    if (pathname.startsWith('/painel/instalacoes')) return 'instalacoes';
    if (pathname.startsWith('/painel/pessoas')) return 'pessoas';
    if (pathname.startsWith('/painel/comissoes')) return 'comissoes';
    if (pathname.startsWith('/painel/config')) return 'config';
    if (pathname.startsWith('/painel/indicacoes')) return 'indicacoes';
    if (pathname.startsWith('/painel/chamados')) return 'chamados';
    return 'visao';
  })();

  const iniciais = (usuario?.nome || '?')
    .split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  const papel = ROTULO_PERFIL[perfilAtivo];

  return (
    <div className="app-shell">
      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setAberto(true)} aria-label="Abrir menu"><Menu /></button>
        <Logo tamanho={28} tamanhoFonte={19} />
        <div className="avatar" style={{ width: 34, height: 34, fontSize: 13 }}>{iniciais}</div>
      </div>

      {aberto && <div className="scrim" onClick={() => setAberto(false)} />}

      <aside className={`sidebar ${aberto ? 'open' : ''}`}>
        <Logo tamanho={32} tamanhoFonte={21} style={{ padding: '6px 12px 22px' }} />

        <nav className="nav">
          {itens.map((i) => (
            <button
              key={i.chave}
              className={`nav-item ${ativo === i.chave ? 'active' : ''}`}
              disabled={!i.caminho}
              title={i.caminho ? undefined : 'Em breve'}
              onClick={() => { if (!i.caminho) return; setAberto(false); nav(i.caminho); }}
            >
              <span className="marker" /> {i.rotulo}
              {!i.caminho && <span className="selo neutro nav-selo">em breve</span>}
            </button>
          ))}
        </nav>

        <div className="profile-wrap" ref={menuRef}>
          <button className="profile-card" onClick={() => setMenu((v) => !v)} aria-expanded={menu}>
            <div className="avatar" style={{ width: 38, height: 38, fontSize: 14 }}>{iniciais}</div>
            <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
              <div className="profile-nome">{usuario?.nome}</div>
              <div className="profile-papel">
                {papel}{contexto && contexto.tipo !== 'admin' ? ` · ${contexto.rotulo}` : ''}
              </div>
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{menu ? '▾' : '▴'}</span>
          </button>

          {menu && (
            <div className="profile-menu">
              <div className="profile-menu-info">{usuario?.celular}</div>
              {opcoes.length > 1 && (
                <button onClick={() => { setMenu(false); nav('/escolher'); }}>Trocar de conta</button>
              )}
              <button className="sair" onClick={() => { sair(); nav('/entrar'); }}>Sair</button>
            </div>
          )}
        </div>

        <div className="sidebar-marca">
          um produto <strong>Grupo Atendi9</strong>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
